/**
 * Notification sound utility
 * Handles playing notification sounds with browser compatibility and error handling
 */

let notificationAudio: HTMLAudioElement | null = null

/**
 * Initializes the notification audio instance
 * Call this once when the app starts to preload the audio
 */
export function initializeNotificationAudio(): void {
    if (notificationAudio) return

    try {
        notificationAudio = new Audio('/src/assets/sounds/notification.mp3')
        notificationAudio.volume = 0.7
        notificationAudio.preload = 'auto'
    } catch {
        // eslint-disable-next-line no-console
        console.warn('Failed to initialize notification audio')
        notificationAudio = null
    }
}

/**
 * Plays the notification sound
 * Safe to call - handles browser autoplay restrictions gracefully
 */
export function playNotificationSound(): void {
    if (!notificationAudio) {
        initializeNotificationAudio()
    }

    if (!notificationAudio) return

    try {
        // Reset audio to beginning if it was played before
        notificationAudio.currentTime = 0

        // Play returns a Promise in modern browsers
        const playPromise = notificationAudio.play()

        if (playPromise !== undefined) {
            playPromise.catch(() => {
                // Autoplay blocked or other error - fail silently
                // eslint-disable-next-line no-console
                console.debug('Notification sound blocked')
            })
        }
    } catch {
        // Fallback for older browsers or other errors
        // eslint-disable-next-line no-console
        console.debug('Failed to play notification sound')
    }
}

/**
 * Checks if the current tab/document is hidden
 */
export function isTabHidden(): boolean {
    return document.visibilityState === 'hidden'
}
